import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/ui-components/ui/button';
import { Badge } from '@/ui-components/ui/badge';
import useApiRequest from '@/lib/hooks/use-request';
import { DealHistoryResponseDto, DealHistoryType } from '@/lib/interfaces/deals';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui-components/ui/select';
import { Input } from '@/ui-components/ui/input';
import { Card, CardContent } from '@/ui-components/ui/card';
import { Calendar, ChevronDown, Filter, History, MessageSquare, Search, User } from 'lucide-react';
import { formatDateTime } from '@/lib/hooks/utils';

type SelectableUser = { _id: string; value: string };

const ActivityIcon: React.FC<{ type?: DealHistoryType }> = ({ type }) => {
  switch (type) {
    case DealHistoryType.ACTIVITY:
      return <Calendar className="h-3 w-3" />;
    case DealHistoryType.NOTE:
    case DealHistoryType.HISTORY_NOTE:
    default:
      return <MessageSquare className="h-3 w-3" />;
  }
};

export default function UserHistoryPage() {
  const { apiRequest } = useApiRequest();
  const [users, setUsers] = useState<SelectableUser[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [items, setItems] = useState<DealHistoryResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState<'all' | 'notes' | 'owner' | 'appointments'>('all');

  const filteredUsers = useMemo(
    () => users.filter(u => u.value.toLowerCase().includes(search.toLowerCase())),
    [users, search]
  );

  const loadUsers = useCallback(async () => {
    try {
      // Use deals creation-entities which provides selectable users with real Mongo IDs
      const res = await apiRequest<any>('settings/users/active-users', 'GET', { suppressGlobalError: true });
      const apiUsers: SelectableUser[] = res?.data || [];
      if (Array.isArray(apiUsers) && apiUsers.length) {
        setUsers(apiUsers);
        if (!selectedUserId && apiUsers[0]?._id) setSelectedUserId(apiUsers[0]._id);
        return;
      }
    } catch (_) {
      // ignore – UI will show empty list
    }
    // Fallback to provided real users if API doesn’t return them
    const fallbackUsers: SelectableUser[] = [
      { _id: '68d03fc8ac8ba4a6a1cb1e90', value: 'Andi Verkauf' },
      { _id: '68cffa36d9c0ce5e95aba40c', value: 'Dirk Verkauf' },
    ];
    setUsers(fallbackUsers);
    if (!selectedUserId) setSelectedUserId(fallbackUsers[0]._id);
  }, [apiRequest, selectedUserId]);

  const loadHistory = useCallback(async () => {
    if (!selectedUserId) return;
    // Only fetch if we have a valid Mongo ObjectId
    if (!/^[a-fA-F0-9]{24}$/.test(selectedUserId)) {
      setItems([]);
      return;
    }
    setLoading(true);
    const limit = 20; // backend Max limit
    // Use owner scope by default to show histories for deals owned by the user
    const res = await apiRequest<any>(`deals/history/user/${selectedUserId}?limit=${limit}&page=${page}&scope=owner`, 'GET', { suppressGlobalError: true });
    setLoading(false);
    const docs: DealHistoryResponseDto[] = res?.data?.docs || res?.data || [];
    setItems(docs);
    const totalDocs = res?.data?.totalDocs;
    if (typeof totalDocs === 'number') {
      setHasMore((page + 1) * limit < totalDocs);
    } else {
      setHasMore(docs.length === limit);
    }
  }, [apiRequest, page, selectedUserId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Real-time: listen for global history updates
  useEffect(() => {
    const handler = () => loadHistory();
    window.addEventListener('historyUpdated', handler);
    return () => window.removeEventListener('historyUpdated', handler);
  }, [loadHistory]);

  const handleRefresh = useCallback(() => {
    // reset to first page and force re-fetch
    setPage(0);
    loadHistory();
  }, [loadHistory]);

  const displayed = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'notes') return items.filter(i => i.historyType === DealHistoryType.NOTE || i.historyType === DealHistoryType.HISTORY_NOTE);
    if (filter === 'appointments') return items.filter(i => i.historyType === DealHistoryType.ACTIVITY);
    if (filter === 'owner') return items.filter(i => (i.historyNoteContent?.description || '').toLowerCase().includes('besitzer'));
    return items;
  }, [items, filter]);

  const selectedUserName = users.find(u => u._id === selectedUserId)?.value || '';

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="px-6 pt-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <User className="h-6 w-6 text-gray-500" />
              <div className="leading-tight">
                <div className="text-base font-medium text-gray-900">{selectedUserName}</div>
                <div className="text-xs text-gray-500">Nutzerübersicht</div>
              </div>
              <div className="relative">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setOpen(o => !o)}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
                {open && (
                  <Card className="absolute z-20 mt-2 w-80">
                    <CardContent className="p-3 space-y-2">
                      <div className="text-sm font-medium">Benutzer durchsuchen</div>
                      <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-gray-400" />
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen..." className="h-8" />
                      </div>
                      <Select value={selectedUserId} onValueChange={(v) => { setSelectedUserId(v); setOpen(false); setPage(0); }}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Benutzer wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredUsers.map(u => (
                            <SelectItem key={u._id} value={u._id}>{u.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>
                )}
              </div>
              <Badge variant="secondary">1 Follower</Badge>
            </div>

            <div className="flex items-center gap-2">
              <Button variant={filter==='all'? 'default': 'outline'} size="sm"><History className="h-4 w-4 mr-1"/>Updates</Button>
              <Button variant="outline" size="sm" disabled>Folge ich</Button>
              <Button variant="outline" size="sm" disabled>Benachrichtigungen</Button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-gray-500" />
            <Button size="sm" variant={filter==='all'? 'default':'outline'} onClick={()=>setFilter('all')}>Alle</Button>
            <Button size="sm" variant={filter==='notes'? 'default':'outline'} onClick={()=>setFilter('notes')}>Nur Notizen</Button>
            <Button size="sm" variant={filter==='appointments'? 'default':'outline'} onClick={()=>setFilter('appointments')}>Termine</Button>
            <Button size="sm" variant={filter==='owner'? 'default':'outline'} onClick={()=>setFilter('owner')}>Owner-Änderungen</Button>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={handleRefresh}>Aktualisieren</Button>
          </div>

          <div className="bg-white border rounded-md">
            <div className="p-3">
              {loading ? (
                <div className="text-sm text-gray-500">Lädt…</div>
              ) : (
                <div className="relative pl-6 grid gap-3 grid-cols-[min-content_1fr]">
                  {displayed.length ? displayed.map((item) => (
                    <React.Fragment key={item._id}>
                      <div className="w-full relative flex justify-center">
                        <div className="w-6 h-6 rounded-full bg-white border border-gray-300 flex items-center justify-center">
                          <ActivityIcon type={item.historyType} />
                        </div>
                      </div>
                      <div className="w-full">
                        <div className="border rounded-md p-3 shadow-sm">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>{item.belongsToUserFullName}</span>
                            <span>{item.createdAt ? formatDateTime(item.createdAt) : ''}</span>
                          </div>
                          <div className="text-sm text-gray-900 whitespace-pre-wrap">
                            {item.historyNoteContent?.description || (item.historyType === DealHistoryType.ACTIVITY ? 'Aktivität' : 'Eintrag')}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  )) : (
                    <div className="text-sm text-gray-500">Keine Einträge</div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between px-3 py-2 border-t bg-gray-50">
              <div className="text-xs text-gray-500">Seite {page+1}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page===0} onClick={()=>setPage(p=>Math.max(0,p-1))}>Zurück</Button>
                <Button size="sm" variant="outline" disabled={!hasMore} onClick={()=>setPage(p=>p+1)}>Weiter</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


